import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { toMMSS } from '../shared/utils/monitor.utils';
import {
  VC_SIGNAL_SPECS,
  VcLevel,
  VcScoreState,
  VcSignalEvent,
  VcSignalFamily,
  VcSignalId,
  VcSignalSpec,
} from './watchers/vc-signals.types';

/** Lookup O(1) de spec por id — se arma una sola vez desde la tabla canónica. */
const SPEC_BY_ID: ReadonlyMap<VcSignalId, VcSignalSpec> = new Map(
  VC_SIGNAL_SPECS.map((spec) => [spec.id, spec]),
);

/** Estado interno por señal — separado de `VcSignalEvent` porque solo nos
 *  importa el ÚLTIMO `lastAt`/`confidence` que cuenta para el score, no el
 *  historial completo (eso vive en el ring buffer de eventos). */
interface SignalTrackState {
  lastAt: number;
  confidence: number;
}

/** Lo que `snapshotForBackend()` manda al backend (POST /answers-progress,
 *  campo por campo aditivo de `AntiFraudSnapshot` — ver contrato §4.1). */
export interface VcBackendSnapshot {
  conferencing_detected_count: number;
  conferencing_times: string[];
  conferencing_signals: string[];
  conferencing_max_score: number;
  /**
   * SPEC-VISION-FASE-A (corrección post-spec) — desglose del pico de aporte
   * de CADA señal (id → aporte máximo visto, `weight × confidence × decay`
   * en su punto más alto). `conferencing_max_score: 0` no distinguía "ninguna
   * señal" de "una señal chica que decayó", y un score sub-umbral no decía
   * CUÁL señal lo produjo. Opcional/aditivo — no rompe al backend si todavía
   * no lo lee (ver interfaces/questions-response.interface.ts, fuera de mi
   * scope: `AntiFraudSnapshot` acepta esto por asignación estructural, no
   * hace falta tocar esa interfaz para que el campo viaje).
   */
  conferencing_signal_peaks?: Record<string, number>;

  /**
   * SPEC-INTEGRITY-SIGNALS (2026-09-29) — eje de INTEGRIDAD, aditivo y
   * opcional, PENDIENTE DE BACKEND: hoy nada en
   * `ia-eva-ms-ts-admin-get-report-api` lee estos cuatro campos, viajan por
   * asignación estructural igual que `conferencing_signal_peaks` en su
   * momento. Cuando el backend los adopte, la garantía que debe mantener es
   * la misma que acá: `integrity_*` es SOLO informativo, nunca debe fijar
   * `risk_level` ni ningún campo de corte (ver el hardcode de
   * `termination_reason === 'conferencing_tool'` en AdminGetReportBL.ts:407
   * — `integrity_*` NO es eso, y no debe volverse eso sin una decisión de
   * producto explícita).
   *
   * LOS DOS EJES SON DISJUNTOS, campo por campo. `integrity_*` contiene
   * EXCLUSIVAMENTE señales `advisory: true`, y `conferencing_*` EXCLUSIVAMENTE
   * las que no lo son. Ningún id aparece en los dos. Eso no es prolijidad:
   * `conferencing_signals` prende `hasConferencingData` en
   * `AdminGetReportBL.ts:432` y el PDF del informe imprime "Señal de
   * videoconferencia detectada (<id>)" para cualquier id sin etiqueta propia
   * (`adminPdfBuilder.ts:521`). Ver `report()`.
   *
   * `integrity_score`/`integrity_level` — PICO de la sesión (no el valor
   * vivo), simétrico con `conferencing_max_score`. Suma pelada de las señales
   * advisory, SIN el bonus ×1.3 de familias (ver `computeScore`).
   * `integrity_signals` — ids advisory vistos. `integrity_times` — mm:ss
   * (mismo `toMMSS` que `conferencing_times`) de la PRIMERA entrada a
   * `suspect` y a `confirmed` del eje de integridad, máximo 2 entradas (ver
   * `trackIntegrityLevelTransition`: ese nivel no tiene histéresis, así que
   * sin el tope repiquetearía).
   */
  integrity_score?: number;
  integrity_level?: string;
  integrity_signals?: string[];
  integrity_times?: string[];
  /** Contraparte advisory de `conferencing_signal_peaks` — pico de aporte por
   *  señal del eje de integridad. Separado a propósito: ver `report()`. */
  integrity_signal_peaks?: Record<string, number>;
}

// Umbrales + histéresis (CONTRATO-VC-DETECTION.md §1 — EXACTOS, no ajustar sin
// actualizar el contrato primero).
const WATCH_ENTER_SCORE = 20;
const WATCH_EXIT_SCORE = 12;
const WATCH_EXIT_SUSTAIN_MS = 60_000;
const SUSPECT_ENTER_SCORE = 45;
const SUSPECT_EXIT_SCORE = 30;
const SUSPECT_EXIT_SUSTAIN_MS = 120_000;
const CONFIRMED_SCORE = 75;
const CONFIRMED_MIN_FAMILIES = 2;
const CONFIRMED_SHORTCUT_WEIGHT = 40;
const CONFIRMED_SHORTCUT_CONFIDENCE = 0.9;

const FAMILY_BONUS_WINDOW_MS = 120_000;
const FAMILY_BONUS_MULTIPLIER = 1.3;

const MAX_EVENTS = 200;
const TICK_MS = 5_000;
/** ~60s (12 ticks de 5s) — heartbeat de calibración (SPEC-VISION-FASE-A). */
const HEARTBEAT_TICKS = 12;

/**
 * SPEC-INTEGRITY-SIGNALS — deriva `integrityLevel` PURO a partir del número
 * (`integrityScore`) y la cantidad de familias distintas que aportan en la
 * ventana. Misma escala/umbrales que `applyHysteresis` (WATCH_ENTER_SCORE/
 * SUSPECT_ENTER_SCORE/CONFIRMED_SCORE/CONFIRMED_MIN_FAMILIES), pero
 * DELIBERADAMENTE sin histéresis, sin sostenido en el tiempo, sin atajo de
 * confirmación por señal aislada y sin ningún estado propio — se recalcula
 * de cero en cada tick. Por diseño NO puede ser sticky ni acumular memoria:
 * es una función pura de (integrityScore, familyCount), nada más entra acá.
 * Esto es justamente lo que garantiza que `integrityLevel` sea un rótulo
 * informativo y nunca una máquina de estados paralela que alguien pueda
 * confundir con enforcement.
 */
/*
 * ESCALA DE ESTE EJE, para quien arme el informe del evaluador: la suma de
 * TODOS los weights advisory con confidence 1 y sin decaimiento da 99
 * (10+20+15+20+12+22), y ya no hay bonus multiplicativo (ver `computeScore`).
 * O sea el techo práctico es ~99 y los umbrales 20/45/75 caen en el 20%/45%/
 * 76% de ese techo.
 *
 * Y una advertencia que NO es paranoia: `integrityLevel` reusa los MISMOS
 * rótulos que el eje de conferencing ('watch'/'suspect'/'confirmed'), pero NO
 * significan lo mismo. Ahí 'suspect' quiere decir "hay evidencia de una
 * herramienta de videoconferencia activa"; acá quiere decir "se acumularon
 * señales de comportamiento sin calibrar". Un evaluador que ya conoce el
 * primer significado va a leer el segundo con esa vara. Quien muestre este
 * campo en una UI tiene que redactarlo en sus propios términos y mostrar el
 * desglose (`integrity_signal_peaks`), no el rótulo pelado.
 */
function deriveIntegrityLevel(integrityScore: number, integrityFamilyCount: number): VcLevel {
  if (integrityScore >= CONFIRMED_SCORE && integrityFamilyCount >= CONFIRMED_MIN_FAMILIES) {
    return 'confirmed';
  }
  if (integrityScore >= SUSPECT_ENTER_SCORE) return 'suspect';
  if (integrityScore >= WATCH_ENTER_SCORE) return 'watch';
  return 'clear';
}

/**
 * VcScorerService — núcleo de scoring de detección de videoconferencia.
 *
 * Recibe señales puntuales (`report()`) de los watchers/servicios de detección
 * (device virtual, segunda voz, caras en pantalla, etc.), las combina con
 * decaimiento exponencial en un score único y expone el nivel resultante
 * (`clear` → `watch` → `suspect` → `confirmed`) con histéresis para no
 * flaquear ante ruido puntual. Puramente de cálculo — no decide enforcement
 * (avisos/suspensión), eso lo arma el consumidor (quiz/fc-session) mirando
 * `state$`.
 *
 * Contrato de arranque: `reset()` fija `testStartMs = Date.now()` — quien
 * integra el scorer (quiz/fc-session) debe llamarlo una vez apenas arranca el
 * test, igual que `AntiCheatService.start()`, para que `conferencing_times`
 * quede en mm:ss relativo al inicio real y no a cuando bootstreó el singleton
 * de Angular. Sin ese llamado, el default (construcción del servicio) sigue
 * siendo un `Date.now()` razonable — no rompe, solo puede quedar corrido.
 */
@Injectable({ providedIn: 'root' })
export class VcScorerService {
  private readonly stateSubject: BehaviorSubject<VcScoreState>;
  readonly state$: Observable<VcScoreState>;

  private readonly signalStates = new Map<VcSignalId, SignalTrackState>();
  private readonly signalsSeen = new Set<VcSignalId>();
  private eventsBuffer: VcSignalEvent[] = [];
  /** Pico de aporte (`weight × confidence × decay`) visto por CADA señal —
   *  ver docblock de `VcBackendSnapshot.conferencing_signal_peaks`. Mismo
   *  comportamiento ACUMULATIVO que `maxScoreSeen`: no se limpia en cada
   *  snapshot por área (ver nota en `sendAntiFraudSnapshot()` de
   *  fc-session.component.ts) — solo `reset()` lo reinicia. Es lo correcto
   *  para un "pico de toda la sesión", pero si un consumidor backend no lo
   *  sabe, va a contar el mismo pico N veces (una por cada snapshot de área
   *  que lo incluya) — documentado acá para que no sea sorpresa.
   */
  private readonly peakContribution = new Map<VcSignalId, number>();
  private tickCount = 0;

  private level: VcLevel = 'clear';
  private watchBelowSince: number | null = null;
  private suspectBelowSince: number | null = null;

  private suspectEntryCount = 0;
  private levelEntryTimes: string[] = [];
  private maxScoreSeen = 0;
  /**
   * Pico del eje de integridad y el nivel que le correspondía en ese momento.
   *
   * Existen porque `integrityScore` es un valor VIVO que decae: con
   * half-lives de 180-300s, un snapshot mandado al cerrar un área después de
   * 40 minutos reporta un número cercano a 0 aunque el eje haya estado en 70
   * a mitad de la prueba. `conferencing_max_score` ya resuelve esto del otro
   * lado siendo un pico — el eje nuevo tiene que ser simétrico o el
   * evaluador lee un número que no representa nada de lo que pasó.
   */
  private maxIntegrityScoreSeen = 0;
  private maxIntegrityLevelSeen: VcLevel = 'clear';
  private testStartMs = Date.now();

  // ── Eje de INTEGRIDAD (SPEC-INTEGRITY-SIGNALS, aditivo) ───────────────────
  // Espejo liviano del estado de enforcement de arriba, pero SIN histéresis
  // ni stickiness (ver docblock de `VcScoreState.integrityLevel`): no hay
  // watchBelowSince/suspectBelowSince acá porque `integrityLevel` no tiene
  // "salida sostenida" — se recalcula puro en cada tick. `integrityLevel`
  // solo se guarda para poder detectar la TRANSICIÓN (para
  // `integrity_times`) y para exponerlo en `state`; nunca se lee para
  // decidir nada (ver `deriveIntegrityLevel`, función pura sin memoria).
  private integrityLevel: VcLevel = 'clear';
  private readonly integritySignalsSeen = new Set<VcSignalId>();
  private integrityLevelEntryTimes: string[] = [];
  /** Niveles de integridad ya registrados en `integrityLevelEntryTimes` —
   *  acota ese array a 2 entradas como máximo. Ver
   *  `trackIntegrityLevelTransition` para el porqué. */
  private readonly integrityLevelsSeen = new Set<VcLevel>();

  private tickTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly ngZone: NgZone) {
    this.stateSubject = new BehaviorSubject<VcScoreState>({
      score: 0,
      level: 'clear',
      families: [],
      events: [],
      integrityScore: 0,
      integrityLevel: 'clear',
      integrityFamilies: [],
    });
    this.state$ = this.stateSubject.asObservable();
    this.startTicking();
  }

  get state(): VcScoreState {
    return this.stateSubject.value;
  }

  /**
   * Ingresa una señal puntual. Aplica cooldown (mismo `id` dentro de su
   * `cooldownMs` refresca `lastAt` pero no vuelve a sumar — ver spec de cada
   * señal en `VC_SIGNAL_SPECS`), evalúa el atajo de confirmación directa
   * (señal aislada con weight≥40 y confidence≥0.9, NUNCA si `spec.advisory`
   * — ver el guard más abajo) y recalcula score/integrityScore/nivel.
   */
  report(event: VcSignalEvent): void {
    // Observabilidad: sin esto la feature es INDIAGNOSTICABLE en un ambiente
    // desplegado — corre, evalúa y no deja rastro, así que no hay forma de
    // distinguir "no detectó nada" de "no corrió". Aprendido a la mala en la
    // primera prueba en dev (2026-09-26).
    console.log('[VC-SIGNAL]', event.id, 'confidence=', event.confidence, event.detail);
    this.pushEvent(event);

    const spec = SPEC_BY_ID.get(event.id);
    // ponytail: un id fuera de VC_SIGNAL_SPECS no debería pasar los tipos —
    // si igual llega (JS sin chequeo, mock roto), solo queda en el ring
    // buffer de auditoría y no participa del score. No lanzamos error acá
    // porque el reporte de señales no debe poder tirar abajo un watcher.
    // Tampoco entra a ningún Set de auditoría: sin spec no sabemos si es
    // advisory, y ante la duda NO contamina el canal de conferencing.
    if (!spec) return;

    // SPEC-INTEGRITY-SIGNALS — LOS DOS SETS SON DISJUNTOS, y esto NO es un
    // detalle de prolijidad: `signalsSeen` viaja al backend como
    // `conferencing_signals`, y ese campo tiene consumidores que le dan
    // semántica de "herramienta de videoconferencia detectada":
    //
    //   - admin-get-report-api/src/domain/AdminGetReportBL.ts:432 —
    //     `hasConferencingData` se prende con que el array tenga UN elemento,
    //     y eso hace aparecer el bloque `conferencing_*` entero en el informe.
    //   - generate-pdf/.../adminPdfBuilder.ts:521 — el fallback para un id
    //     sin etiqueta conocida imprime literalmente
    //     "Señal de videoconferencia detectada (<id>)".
    //
    // O sea: meter 'gaze-off-sustained' acá hace que mirar 3 segundos fuera
    // de pantalla salga impreso como videoconferencia detectada, en un PDF
    // que puede terminar en manos del candidato. Toda la separación de ejes
    // de este archivo no sirve de nada si los ids se filtran por el canal de
    // auditoría. Advisory va a `integritySignalsSeen` y SOLO ahí.
    if (spec.advisory) {
      this.integritySignalsSeen.add(event.id);
    } else {
      this.signalsSeen.add(event.id);
    }

    const prev = this.signalStates.get(event.id);
    const withinCooldown = !!prev && event.at - prev.lastAt < spec.cooldownMs;
    if (withinCooldown) {
      // Refresca lastAt (mantiene viva la señal, resetea su propio
      // decaimiento) pero NO pisa confidence — no "suma de nuevo".
      prev!.lastAt = event.at;
    } else {
      this.signalStates.set(event.id, { lastAt: event.at, confidence: event.confidence });
    }

    // GARANTÍA DE SEGURIDAD (SPEC-INTEGRITY-SIGNALS): `!spec.advisory` va
    // PRIMERO y a propósito. Este atajo bypasea por completo `computeScore()`
    // (fuerza `this.level = 'confirmed'` directo en `applyHysteresis`) —
    // sin este guard, una señal advisory con weight≥40 y confidence≥0.9
    // podría confirmar aunque `computeScore()` la excluya correctamente del
    // score. Hoy NINGUNA fila advisory llega a weight 40 (la más alta es
    // voice-mismatch en 22 — ver VC_SIGNAL_SPECS), pero este guard es lo que
    // sigue siendo cierto aunque eso cambie mañana.
    const isShortcut =
      !spec.advisory &&
      spec.weight >= CONFIRMED_SHORTCUT_WEIGHT &&
      event.confidence >= CONFIRMED_SHORTCUT_CONFIDENCE;

    // ponytail: report() no hace la danza de ngZone (a diferencia de tick())
    // — el contrato solo la pide para el interval interno. Los watchers que
    // llaman report() son responsables de su propia zona (§6 del contrato);
    // si alguno reporta desde afuera de Angular, el tick de 5s igual fuerza
    // la reentrada dentro de ese margen. Si en integración se ve un modal de
    // aviso tardando hasta 5s en aparecer, mover esta misma dupla acá.
    this.recomputeAndEmit(event.at, isShortcut);
  }

  /** Recalcula decaimiento aunque no haya llegado ningún evento nuevo — es lo
   *  que permite que la histéresis de salida (sostenida en el tiempo) avance
   *  incluso en silencio. */
  private tick(): void {
    const prevLevel = this.level;
    this.recomputeAndEmit(Date.now());

    // Heartbeat de calibración (SPEC-VISION-FASE-A) — cada ~60s (12 ticks de
    // 5s), independiente de si hubo cambio de nivel. Mismo motivo que el log
    // de `report()`: sin esto, "score 0 toda la sesión" y "el scorer nunca
    // corrió" son indistinguibles en un ambiente desplegado.
    this.tickCount++;
    if (this.tickCount % HEARTBEAT_TICKS === 0) {
      const { score, level, families, integrityScore, integrityLevel } = this.stateSubject.value;
      // SPEC-INTEGRITY-SIGNALS: el heartbeat ahora también loguea el eje de
      // integridad — sin esto repetimos EXACTAMENTE el error que ya pagamos
      // una vez con el score de enforcement (ver docblock de la clase,
      // report() y el comentario original de este heartbeat): "integrity
      // score 0 toda la sesión" y "las señales advisory nunca corrieron"
      // volverían a ser indistinguibles en un ambiente desplegado.
      console.log(
        `[VC-SCORER] hb score=${score.toFixed(1)} level=${level} familias=[${families.join(',')}] señales=${this.signalStates.size} pico=${this.maxScoreSeen.toFixed(1)} integrity=${integrityScore.toFixed(1)} integrityLevel=${integrityLevel}`,
      );
    }

    if (this.level !== prevLevel) {
      // El interval corre en runOutsideAngular (ver startTicking) — reentramos
      // acá solo cuando el nivel efectivamente cambió, para que Angular note
      // el cambio (modal de aviso, etc.) sin pagar un ciclo de CD cada 5s.
      this.ngZone.run(() => {});
    }
  }

  // `forceConfirmed` es el atajo de señal aislada (weight≥40, confidence≥0.9)
  // — se decide en report() mirando SOLO el evento entrante, pero se aplica
  // acá adentro (no seteando `this.level` directo en report()) para que
  // `prevLevel` quede bien capturado ANTES del salto y trackLevelTransition
  // pueda ver el cambio real (si no, una entrada directa a confirmed nunca
  // quedaría registrada en `conferencing_times`).
  private recomputeAndEmit(now: number, forceConfirmed = false): void {
    const { score, families, integrityScore, integrityFamilies } = this.computeScore(now);
    this.maxScoreSeen = Math.max(this.maxScoreSeen, score);
    this.maxIntegrityScoreSeen = Math.max(this.maxIntegrityScoreSeen, integrityScore);
    if (integrityScore >= this.maxIntegrityScoreSeen) {
      // El nivel se guarda junto al pico, no por separado: reportar el pico
      // de score con el nivel ACTUAL daría pares incoherentes (score 70 con
      // nivel 'clear' después de decaer).
      this.maxIntegrityLevelSeen = deriveIntegrityLevel(integrityScore, integrityFamilies.length);
    }

    const prevLevel = this.level;
    this.applyHysteresis(score, families, now, forceConfirmed);
    this.trackLevelTransition(prevLevel, this.level, now);

    // SPEC-INTEGRITY-SIGNALS: `integrityLevel` se deriva PURO del número en
    // cada tick (sin histéresis/stickiness, ver deriveIntegrityLevel) — a
    // propósito NUNCA pasa por `applyHysteresis` ni toca `this.level`. Solo
    // se guarda para poder loguear la transición y exponerla en `state`.
    const prevIntegrityLevel = this.integrityLevel;
    this.integrityLevel = deriveIntegrityLevel(integrityScore, integrityFamilies.length);
    this.trackIntegrityLevelTransition(prevIntegrityLevel, this.integrityLevel, now);

    // El score vivo es EL dato de calibración: con esto se mide qué genera un
    // candidato honesto antes de bajar los umbrales. Sin el log hay que
    // adivinarlo. Solo en cambio de nivel para no inundar la consola.
    if (prevLevel !== this.level) {
      console.log(
        `[VC-SCORER] ${prevLevel} → ${this.level}  score=${score.toFixed(1)}  familias=[${families.join(',')}]  pico=${this.maxScoreSeen.toFixed(1)}  integrity=${integrityScore.toFixed(1)}(${this.integrityLevel})`
      );
    }

    this.stateSubject.next({
      score,
      level: this.level,
      families,
      events: [...this.eventsBuffer],
      integrityScore,
      integrityLevel: this.integrityLevel,
      integrityFamilies,
    });
  }

  /**
   * aporte(s) = weight(s) × confidence(último evento de s) × 2^(-(now - lastAt(s)) / halfLife(s))
   *
   * score          = Σ aportes (SOLO señales NO advisory — esto gobierna
   *                  enforcement); si ≥2 familias NO advisory distintas
   *                  aportan > 0 dentro de una ventana de 120s → score × 1.3
   * integrityScore = Σ aportes SOLO de señales advisory (eje DISJUNTO de
   *                  `score`) — SUMA PELADA, SIN el bonus ×1.3: ese bonus es
   *                  exclusivo del eje de enforcement (ver el porqué en el
   *                  comentario largo más abajo, dentro de este mismo método)
   *
   * GARANTÍA DE SEGURIDAD (ver `VcSignalSpec.advisory`): `score`/`families`
   * salen de un acumulador que salta las señales advisory con un `continue`
   * explícito ANTES de sumarlas — no es un filtro post-hoc que se pueda
   * romper reordenando código. Si esto alguna vez deja de ser así, el test
   * dedicado en vc-scorer.service.spec.ts ("garantía de seguridad") lo
   * detecta.
   */
  private computeScore(now: number): {
    score: number;
    families: VcSignalFamily[];
    integrityScore: number;
    integrityFamilies: VcSignalFamily[];
  } {
    let rawScore = 0;
    let rawIntegrityScore = 0;
    const familiesInWindow = new Set<VcSignalFamily>();
    const integrityFamiliesInWindow = new Set<VcSignalFamily>();

    for (const [id, tracked] of this.signalStates) {
      const spec = SPEC_BY_ID.get(id);
      if (!spec) continue;

      const elapsed = now - tracked.lastAt;
      const decay = Math.pow(2, -(elapsed / spec.halfLifeMs));
      const aporte = spec.weight * tracked.confidence * decay;

      // SPEC-VISION-FASE-A (corrección post-spec) — desglose por señal para
      // `conferencing_signal_peaks` (ver VcBackendSnapshot). Puramente
      // observacional: no cambia rawScore/families, no agrega señales nuevas
      // al scorer, no toca umbrales. Se registra para TODAS las señales
      // (advisory incluidas) — es auditoría, no enforcement.
      this.peakContribution.set(id, Math.max(this.peakContribution.get(id) ?? 0, aporte));

      // Eje de integridad: SOLO señales advisory. Los dos ejes son DISJUNTOS,
      // igual que `signalsSeen` / `integritySignalsSeen` y que
      // `conferencing_signal_peaks` / `integrity_signal_peaks` (ver
      // `report()` y `snapshotForBackend()`).
      //
      // Una versión anterior sumaba acá TODAS las señales ("integridad = el
      // total"). Se descartó porque rompía la propiedad que hace legible a
      // este par de números: cada score tiene que cerrar con SU desglose. Con
      // el eje de integridad incluyendo señales de conferencing, un evaluador
      // que mirara `integrity_score` junto a `integrity_signals` veía un
      // número que sus propias señales no explican — y encima con dos
      // fórmulas distintas (el eje de conferencing lleva el bonus ×1.3, este
      // no, ver más abajo), así que ni siquiera era una suma del otro.
      if (spec.advisory) {
        rawIntegrityScore += aporte;
        if (aporte > 0 && elapsed <= FAMILY_BONUS_WINDOW_MS) {
          integrityFamiliesInWindow.add(spec.family);
        }
      }

      // GARANTÍA DE SEGURIDAD: una señal advisory nunca llega a rawScore ni a
      // familiesInWindow — sale del cálculo de enforcement ACÁ, antes de
      // sumar nada. Ver docblock de `VcSignalSpec.advisory`.
      if (spec.advisory) continue;

      rawScore += aporte;
      if (aporte > 0 && elapsed <= FAMILY_BONUS_WINDOW_MS) {
        familiesInWindow.add(spec.family);
      }
    }

    const families = Array.from(familiesInWindow);
    const score = families.length >= 2 ? rawScore * FAMILY_BONUS_MULTIPLIER : rawScore;

    const integrityFamilies = Array.from(integrityFamiliesInWindow);
    // El eje de integridad es SUMA PELADA — SIN el bonus ×1.3 de familias que
    // sí lleva el eje de conferencing. No es un olvido.
    //
    // Ese bonus premia CORROBORACIÓN: dos familias distintas apuntando al
    // mismo hecho desde evidencia INDEPENDIENTE. Las señales advisory no
    // cumplen esa premisa — están fuertemente correlacionadas por causa
    // común. Una webcam mala de laptop en un cuarto con contraluz produce,
    // ella sola: `second-voice` (jawOpen que no sigue la boca → familia
    // audio), `no-face-sustained` (pérdidas de tracking → vision) y
    // `gaze-off-sustained` (ruido de pose → vision). Son dos familias con UNA
    // sola causa física, y multiplicarlas por 1.3 convierte una cámara
    // barata en "corroboración multi-familia".
    //
    // En el candidato honesto del log del 2026-09-29 esto es literalmente la
    // diferencia entre 14.5 ('clear') y 18.85 (a 1.15 puntos de 'watch').
    //
    // `integrityFamilies` se sigue calculando y exponiendo: es dato útil para
    // el evaluador ("¿de cuántos frentes distintos vino esto?"), sólo que no
    // multiplica nada.
    const integrityScore = rawIntegrityScore;

    return { score, families, integrityScore, integrityFamilies };
  }

  /** Máquina de niveles con histéresis exacta del contrato §1. `confirmed` es
   *  sticky — una vez ahí, nunca vuelve a bajar por acá (solo `reset()`). */
  private applyHysteresis(
    score: number,
    families: VcSignalFamily[],
    now: number,
    forceConfirmed: boolean,
  ): void {
    if (this.level === 'confirmed') return;

    const confirmedByThreshold = score >= CONFIRMED_SCORE && families.length >= CONFIRMED_MIN_FAMILIES;
    if (forceConfirmed || confirmedByThreshold) {
      this.level = 'confirmed';
      this.watchBelowSince = null;
      this.suspectBelowSince = null;
      return;
    }

    switch (this.level) {
      case 'clear':
        if (score >= SUSPECT_ENTER_SCORE) {
          this.level = 'suspect';
        } else if (score >= WATCH_ENTER_SCORE) {
          this.level = 'watch';
        }
        break;

      case 'watch':
        if (score >= SUSPECT_ENTER_SCORE) {
          this.level = 'suspect';
          this.watchBelowSince = null;
        } else if (score < WATCH_EXIT_SCORE) {
          if (this.watchBelowSince === null) this.watchBelowSince = now;
          if (now - this.watchBelowSince >= WATCH_EXIT_SUSTAIN_MS) {
            this.level = 'clear';
            this.watchBelowSince = null;
          }
        } else {
          this.watchBelowSince = null;
        }
        break;

      case 'suspect':
        if (score < SUSPECT_EXIT_SCORE) {
          if (this.suspectBelowSince === null) this.suspectBelowSince = now;
          if (now - this.suspectBelowSince >= SUSPECT_EXIT_SUSTAIN_MS) {
            this.level = 'watch';
            this.suspectBelowSince = null;
          }
        } else {
          this.suspectBelowSince = null;
        }
        break;
    }
  }

  /** conferencing_detected_count cuenta ENTRADAS a 'suspect' (cada vez, no
   *  solo la primera). conferencing_times registra mm:ss de cada entrada a
   *  'suspect' O 'confirmed' — ver contrato §4.1. */
  private trackLevelTransition(prevLevel: VcLevel, newLevel: VcLevel, now: number): void {
    if (newLevel === prevLevel) return;

    if (newLevel === 'suspect') {
      this.suspectEntryCount++;
      this.levelEntryTimes.push(toMMSS(now - this.testStartMs));
    } else if (newLevel === 'confirmed') {
      this.levelEntryTimes.push(toMMSS(now - this.testStartMs));
    }
  }

  /**
   * SPEC-INTEGRITY-SIGNALS — equivalente de `trackLevelTransition` para el
   * eje de integridad, pero SOLO bitácora (`integrity_times`): a diferencia
   * de `trackLevelTransition`, esto NUNCA participa de enforcement, no
   * cuenta un `integrityDetectedCount` propio (no se pidió) y no tiene
   * ninguna noción de "sostenido" — `newLevel` ya viene de
   * `deriveIntegrityLevel`, que es puro y sin memoria.
   *
   * Registra la PRIMERA entrada a cada nivel y nada más, así que
   * `integrity_times` tiene como máximo 2 elementos en toda la sesión.
   *
   * Por qué el techo (y por qué `conferencing_times` NO lo necesita): el
   * nivel de conferencing tiene histéresis de salida sostenida y `confirmed`
   * es sticky, así que no puede repiquetear. `integrityLevel` es lo
   * contrario A PROPÓSITO — puro, sin memoria, recalculado en cada tick de
   * 5s — que es exactamente lo que le impide hacer enforcement, pero también
   * lo que lo deja oscilar libremente: un score que decae rondando
   * SUSPECT_ENTER_SCORE cruza para arriba y para abajo indefinidamente. Sin
   * este guard, una sesión larga acumula cientos de entradas y las
   * re-serializa completas en CADA snapshot de área.
   *
   * Y para el evaluador la pregunta útil no es "cuántas veces repiqueteó"
   * (eso es ruido del decaimiento, no conducta del candidato) sino "a partir
   * de cuándo la integridad de esta sesión entró en esa zona".
   */
  private trackIntegrityLevelTransition(prevLevel: VcLevel, newLevel: VcLevel, now: number): void {
    if (newLevel === prevLevel) return;
    if (newLevel !== 'suspect' && newLevel !== 'confirmed') return;
    if (this.integrityLevelsSeen.has(newLevel)) return;
    this.integrityLevelsSeen.add(newLevel);
    this.integrityLevelEntryTimes.push(toMMSS(now - this.testStartMs));
  }

  private pushEvent(event: VcSignalEvent): void {
    this.eventsBuffer.push(event);
    if (this.eventsBuffer.length > MAX_EVENTS) {
      this.eventsBuffer.shift();
    }
  }

  /** Snapshot listo para mergear en `AntiFraudSnapshot` (POST /answers-progress
   *  MCQ, o `fc_anti_fraud_snapshot` en FC) — ver contrato §4.1. No crea
   *  ningún request nuevo, el caller decide cuándo/adónde mandarlo. */
  snapshotForBackend(): VcBackendSnapshot {
    const snapshot: VcBackendSnapshot = {
      conferencing_detected_count: this.suspectEntryCount,
      conferencing_times: [...this.levelEntryTimes],
      conferencing_signals: Array.from(this.signalsSeen),
      conferencing_max_score: this.maxScoreSeen,
    };
    // Omitido cuando está vacío (en vez de mandar `{}`) — preserva el shape
    // exacto de hoy para una sesión sin señales, y es la forma más natural de
    // "opcional" para un desglose que puede no tener nada que desglosar.
    // Los picos se PARTEN por eje, por el mismo motivo que `signalsSeen` e
    // `integritySignalsSeen` son disjuntos (ver `report()`): un id advisory
    // dentro de un campo `conferencing_*` es una acusación de
    // videoconferencia con otro nombre. `conferencing_signal_peaks` queda
    // con exactamente las mismas señales que tenía antes de este cambio.
    const conferencingPeaks: Record<string, number> = {};
    const integrityPeaks: Record<string, number> = {};
    for (const [id, peak] of this.peakContribution) {
      // OJO con la distinción, es sutil y ya me mordió una vez: `advisory` es
      // OPCIONAL, así que una señal NO advisory simplemente lo omite y
      // `spec.advisory` da `undefined`. Eso NO es lo mismo que "no hay spec".
      //   - spec existe → decide `advisory === true` (undefined = conferencing).
      //   - spec NO existe → lado SEGURO: integridad. No debería poder pasar
      //     (`report()` corta con `if (!spec) return` y `peakContribution`
      //     solo se llena después del mismo guard), pero si pasara, un id
      //     desconocido no puede terminar impreso como "Señal de
      //     videoconferencia detectada" en el informe del evaluador.
      const spec = SPEC_BY_ID.get(id);
      const isAdvisory = spec ? spec.advisory === true : true;
      const target = isAdvisory ? integrityPeaks : conferencingPeaks;
      target[id] = Number(peak.toFixed(1));
    }
    if (Object.keys(conferencingPeaks).length > 0) {
      snapshot.conferencing_signal_peaks = conferencingPeaks;
    }
    if (Object.keys(integrityPeaks).length > 0) {
      snapshot.integrity_signal_peaks = integrityPeaks;
    }

    // SPEC-INTEGRITY-SIGNALS — mismo criterio de "omitir si vacío" que
    // conferencing_signal_peaks arriba: `signalStates.size > 0` es el gate
    // más amplio (cualquier señal, advisory o no, reportada alguna vez), así
    // una sesión sin NINGUNA señal sigue devolviendo el shape de hoy
    // (`snapshotForBackend()` sin campos `integrity_*`, ver el test "starts
    // with an empty snapshot").
    //
    // Se manda el PICO de la sesión, no el valor vivo — simétrico con
    // `conferencing_max_score` y por el mismo motivo (ver
    // `maxIntegrityScoreSeen`): el score decae, y un snapshot de cierre de
    // área reportaría casi 0 sobre una sesión que estuvo alta a la mitad.
    if (this.signalStates.size > 0) {
      snapshot.integrity_score = Number(this.maxIntegrityScoreSeen.toFixed(1));
      snapshot.integrity_level = this.maxIntegrityLevelSeen;
    }
    if (this.integritySignalsSeen.size > 0) {
      snapshot.integrity_signals = Array.from(this.integritySignalsSeen);
    }
    if (this.integrityLevelEntryTimes.length > 0) {
      snapshot.integrity_times = [...this.integrityLevelEntryTimes];
    }
    return snapshot;
  }

  /** Limpia todo el estado y fija `testStartMs = Date.now()` — llamar una vez
   *  al arrancar un test nuevo (ver nota de arranque en el docblock de la clase). */
  reset(): void {
    this.signalStates.clear();
    this.signalsSeen.clear();
    this.eventsBuffer = [];
    this.level = 'clear';
    this.watchBelowSince = null;
    this.suspectBelowSince = null;
    this.suspectEntryCount = 0;
    this.levelEntryTimes = [];
    this.maxScoreSeen = 0;
    this.peakContribution.clear();
    this.tickCount = 0;
    this.testStartMs = Date.now();

    // SPEC-INTEGRITY-SIGNALS — mismo reset que el resto del estado, eje de
    // integridad incluido: sin esto una sesión nueva arrancaría con
    // integrity_signals/integrity_times de la sesión ANTERIOR.
    this.integrityLevel = 'clear';
    this.integritySignalsSeen.clear();
    this.integrityLevelEntryTimes = [];
    this.integrityLevelsSeen.clear();
    this.maxIntegrityScoreSeen = 0;
    this.maxIntegrityLevelSeen = 'clear';

    this.stateSubject.next({
      score: 0,
      level: 'clear',
      families: [],
      events: [],
      integrityScore: 0,
      integrityLevel: 'clear',
      integrityFamilies: [],
    });
  }

  /** Pausa del candidato: sin ticks, el scorer no emite niveles ni alertas. */
  pauseTicking(): void {
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = null;
  }

  resumeTicking(): void {
    if (!this.tickTimer) this.startTicking();
  }

  private startTicking(): void {
    if (typeof window === 'undefined') return; // SSR: sin timers
    try {
      if ((window as any).__karma__) return; // Karma: evita timers colgados en tests
    } catch (e) {}

    this.ngZone.runOutsideAngular(() => {
      this.tickTimer = setInterval(() => this.tick(), TICK_MS);
    });
  }
}
